import { useEffect, useRef, useState } from "react";
import {
  LiaHornbill,
  LiaSearchSolid,
} from "react-icons/lia";
import { MdClose, MdPrint } from "react-icons/md";
import { orderPlanView, salesOrderMaster } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import BatchTable from "./BatchTable";
import { formatDate } from "../../utlis/formateDate";
import Model from "../../common/Model";
import { STATUS_COLORS } from "../../utlis/constant";
import { RegistryHeader, Button as SharedButton, CalculationFooter } from "../../common/Shared";

const OrderPlanView = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOrderplanData, setIsOrderPlanData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [batchItems, setBatchItems] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [viewData, setViewData] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("sales");
  const [salesPage, setSalesPage] = useState(1);
  const [salesHasMore, setSalesHasMore] = useState(true);
  const [executedData, setExecutedData] = useState([]);
  const [executedPage, setExecutedPage] = useState(1);
  const [executedHasMore, setExecutedHasMore] = useState(true);
  const pageSize = 50;
  const tableScrollRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchOrderplanData = async (page) => {
    try {
      setLoading(true);
      const res = await orderPlanView.getAllPlanOrders(page, pageSize);

      setIsOrderPlanData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setSalesHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutedOrders = async (page) => {
    try {
      setLoading(true);
      const res = await orderPlanView.getAllExecutedOrders(page, pageSize);

      setExecutedData((prev) => {
        return page === 1 ? res.items || [] : [...prev, ...(res.items || [])];
      });

      if (!res.items || res.items.length < pageSize) {
        setExecutedHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "sales") {
      setSalesPage(1);
      setIsOrderPlanData([]);
      setSalesHasMore(true);
    } else {
      setExecutedPage(1);
      setExecutedData([]);
      setExecutedHasMore(true);
    }
  };

  useEffect(() => {
    if (activeTab === "sales") fetchOrderplanData(salesPage);
  }, [salesPage, activeTab]);

  useEffect(() => {
    if (activeTab === "executed") fetchExecutedOrders(executedPage);
  }, [executedPage, activeTab]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;

        if (activeTab === "sales" && salesHasMore) {
          setSalesPage((p) => p + 1);
        }

        if (activeTab === "executed" && executedHasMore) {
          setExecutedPage((p) => p + 1);
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeTab, loading, salesHasMore, executedHasMore]);

  const handleView = async (id) => {
    setLoading(true);
    try {
      const res = await salesOrderMaster.getById(id);
      setViewData(res.data);
      setIsViewModalOpen(true);
    } catch (err) {
      notify("Failed to load order details", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setIsOrderPlanData(isOrderplanData);
    } else {
      const filtered = isOrderplanData.filter((d) =>
        d.status_Name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setIsOrderPlanData(filtered);
    }
  }, [searchQuery, isOrderplanData]);

  const handlePrint = async (order_No) => {
    try {
      setLoading(true);

      const blob = await orderPlanView.getByPrintId(order_No);
      if (!blob) {
        notify("No PDF received from server", "warning");
        return;
      }

      const file = new Blob([blob], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      const printWindow = window.open(fileURL, "_blank");

      printWindow.onload = () => {
        printWindow.print();
      };

      notify("PDF opened successfully", "success");
    } catch (err) {
      console.error("Print Error:", err);
      notify("Failed to open PDF", "error");
    } finally {
      setLoading(false);
    }
  };

  const executedColumns = [
    { key: "order_No", label: "Order No" },
    { key: "batch_Id", label: "Batch Id" },
    { key: "total_Qty", label: "Qty" },
    {
      key: "order_Date",
      label: "Ord Date",
      render: (value) => formatDate(value),
    },
    {
      key: "dispatch_Date",
      label: "Dispatch Date",
      render: (value) => formatDate(value),
    },
    { key: "general_Notes", label: "Remarks" },
  ];

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          checked={
            isOrderplanData.length > 0 &&
            selectedIds.length ===
            isOrderplanData.filter((i) => i?.id).map((i) => i.id).length
          }
          onChange={(e) => {
            if (e.target.checked) {
              const allIds = isOrderplanData
                .filter((i) => i?.id)
                .map((i) => i.id);
              setSelectedIds(allIds);
            } else {
              setSelectedIds([]);
            }
          }}
        />
      ),
      render: (_value, row) =>
        row?.id ? (
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id)}
            onChange={() => {
              setSelectedIds((prev) =>
                prev.includes(row.id)
                  ? prev.filter((id) => id !== row.id)
                  : [...prev, row.id],
              );
            }}
          />
        ) : null,
    },
    { key: "order_No", label: "SO NO" },
    {
      key: "die_Status",
      label: "DIE STATUS",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-3 py-1 border rounded-none text-[9px] font-black uppercase tracking-[2px] ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "cust_Name", label: "CUSTOMER" },
    { key: "dieNumber", label: "DIE NO" },
    { key: "currentWeight", label: "CURRENT WT" },
    { key: "profile_Length", label: "CUT LENGTH" },
    { key: "qty", label: "PCS" },
    { key: "qty_Weight", label: "WT" },
    { key: "alloy_Name", label: "ALLOY" },
    { key: "req_Delivery", label: "REQ. DELIVERY" },
    { key: "status", label: "DISPATCH DAYS." },
  ];

  const DetailItem = ({ label, value, highlight }) => (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${highlight ? "text-[#3F80F7]" : "text-slate-800"}`}
      >
        {value || "—"}
      </span>
    </div>
  );

  // Pre-calculate summary stats for the footer
  const totalLoadPcs = isOrderplanData.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
  const totalLoadWeight = isOrderplanData.reduce((sum, item) => sum + (Number(item.qty_Weight) || 0), 0);
  const selectedPcs = isOrderplanData.filter((item) => selectedIds.includes(item.id)).reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
  const selectedWeight = isOrderplanData.filter((item) => selectedIds.includes(item.id)).reduce((sum, item) => sum + (Number(item.qty_Weight) || 0), 0);
  const hasSelection = selectedIds.length > 0;

  return (
    <div className="flex flex-col h-full bg-white font-plus">
      <RegistryHeader
        title="Order Planning"
      >
        <div className="flex bg-gray-100 p-1 rounded-sm gap-1">
          <button
            onClick={() => handleTabChange("sales")}
            className={`px-8 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "sales"
              ? "bg-[#3F80F7] text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Sales Orders
          </button>
          <button
            onClick={() => handleTabChange("executed")}
            className={`px-8 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "executed"
              ? "bg-[#3F80F7] text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Executed Orders
          </button>
        </div>

        {activeTab === "sales" && (
          <>
            <div className="relative w-full sm:w-80 group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
                <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
              </div>
              <input
                type="text"
                className="block w-full pl-12 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
                placeholder="Search Orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                >
                  <MdClose size={18} />
                </button>
              )}
            </div>

            <SharedButton
              disabled={selectedIds.length === 0 || isSaving}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                setIsSaving(true);
                try {
                  const res = await salesOrderMaster.AddToBatch({ SelectedIds: selectedIds });
                  setBatchItems(res || []);
                  setBatchId(res.batchId || "");
                  setIsModelOpen(true);
                } catch (err) {
                  notify("Failed to add to batch", "error");
                } finally {
                  setIsSaving(false);
                }
              }}
              icon={LiaHornbill}
              className="whitespace-nowrap"
            >
              {isSaving ? "PROCESSING..." : `Add To Batch [${selectedIds.length}]`}
            </SharedButton>
          </>
        )}
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >
          {loading && (salesPage === 1 || executedPage === 1) && <Loader centered />}
          {activeTab === "sales" ? (
            <div className="min-w-max">
              <Table
                columns={columns}
                data={isOrderplanData}
                showActions={false}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          ) : (
            <div className="min-w-max">
              <Table
                columns={executedColumns}
                data={executedData}
                onView={(row) => handleView(row.order_No)}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          )}
        </div>
      </div>

      {activeTab === "sales" && (
        <CalculationFooter
          items={[
            {
              label: "TOTAL PCS",
              value: totalLoadPcs.toLocaleString(),
              suffix: "PCS"
            },
            {
              label: "TOTAL WT",
              value: totalLoadWeight.toFixed(3),
              suffix: "KG",
              color: "text-[#3F80F7]"
            },
            {
              label: "SELECTED PCS",
              value: selectedPcs.toLocaleString(),
              suffix: "PCS",
              color: "text-slate-800"
            },
            {
              label: "SELECTED WT",
              value: selectedWeight.toFixed(3),
              suffix: "KG",
              color: "text-[#3F80F7]"
            }
          ]}
        />
      )}

      {isModelOpen && (
        <BatchTable
          batchItems={batchItems}
          batchId={batchId}
          onClose={() => {
            setIsModelOpen(false);
            setIsOrderPlanData([]);
            if (salesPage === 1) fetchOrderplanData(1);
            else setSalesPage(1);
            setSalesHasMore(true);
            setSelectedIds([]);
            setBatchItems([]);
            setBatchId("");
          }}
        />
      )}

      <Model
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        name="Executed Order Details"
      >
        {viewData && (
          <div className="flex flex-col p-2 text-plus">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 bg-gray-50 border border-gray-200 p-8 mb-8 font-plus">
              <DetailItem label="Order Number" value={viewData.order_No} highlight />
              <DetailItem label="Customer Name" value={viewData.customerName} />
              <DetailItem label="Order Alloy" value={viewData.aolly_Name} />
              <DetailItem label="Order Date" value={formatDate(viewData.order_Date)} />
              <DetailItem llabel="Delivery Days" value={`${viewData.delivery_Days} Days`} />
              <DetailItem label="Calculated Dispatch" value={formatDate(viewData.calculate_dispatch_date)} />
            </div>
            <div className="mb-10 font-plus">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <span className="h-6 w-1 bg-[#3F80F7]"></span>
                  <h3 className="text-[12px] font-black text-gray-800 uppercase tracking-[3px]">
                    Order Items
                  </h3>
                </div>
                <button
                  className="bg-gray-900 text-white px-8 py-2.5 text-[10px] font-black uppercase tracking-[3px] hover:bg-black flex items-center gap-3 transition-all"
                  onClick={() => handlePrint(viewData.order_No)}
                >
                  <MdPrint size={16} /> Print Invoice"
                </button>
              </div>

              <div className="overflow-hidden border border-gray-200 bg-white">
                <Table
                  columns={[
                    { key: "dieNumber", label: "Die Profile", render: (v) => <span className="font-bold uppercase">{v}</span> },
                    { key: "currentWeight", label: "Wt", align: "center", render: (v) => <span className="font-bold">{v}</span> },
                    { key: "unit_Conv_Name", label: "Unit", align: "center", render: (v) => <span className="font-bold uppercase text-[10px] tracking-widest">{v}</span> },
                    { key: "profile_Length", label: "Length", align: "center", render: (v) => <span className="font-bold">{v}</span> },
                    { key: "qty", label: "Quantity", align: "center", render: (v) => <span className="font-black text-[#3F80F7]">{v}</span> },
                    { key: "qty_Weight", label: "Total weight", align: "center", render: (v) => <span className="font-black">{v}</span> },
                    {
                      key: "powder_Coating_Color",
                      label: "Coating Color",
                      render: (v) => (
                        <span className="uppercase font-bold text-[10px] tracking-widest">
                          {v || "-"}
                        </span>
                      ),
                    },
                  ]}
                  data={viewData?.orderDetails || []}
                  showActions={false}
                  tableClassName="text-[12px] font-bold"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-4 font-plus">
              <div className="space-y-6">
                <div className="border border-amber-100 bg-amber-50/50 p-6">
                  <h4 className="text-[10px] font-black text-amber-800 uppercase tracking-[3px] mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-amber-400 rounded-none"></span> Packing Instructions
                  </h4>
                  <p className="text-[13px] text-gray-700 font-bold italic leading-relaxed">
                    "
                    {viewData.packingInstructions ||
                      "No specific instructions provided."}
                    "
                  </p>
                </div>
                <div className="border border-gray-200 bg-white p-6">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[3px] mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-200 rounded-none"></span>   General Notes
                  </h4>
                  <p className="text-[13px] text-gray-700 font-bold uppercase tracking-tight">
                    {viewData.generalNotes || "-"}
                  </p>
                </div>
              </div>

              <div className="bg-gray-900 p-8 text-white flex flex-col justify-center gap-8 border border-gray-900">
                <h3 className="text-gray-500 text-[10px] font-black uppercase tracking-[4px]"> Order Summary </h3>
                <div className="flex justify-between items-baseline border-b border-gray-800 pb-6">
                  <span className="text-gray-400 text-[12px] font-bold uppercase tracking-[2px]">Total Quantity</span>
                  <span className="text-[32px] font-black leading-none"> {viewData?.total_Qty} <span className="text-[12px] text-gray-600">PCS</span> </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400 text-[12px] font-bold uppercase tracking-[2px]">Estimated Weight</span>
                  <span className="text-[32px] font-black leading-none text-[#3F80F7]"> {viewData?.total_Weight} <span className="text-[12px] text-gray-600">KG</span> </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Model>
    </div>
  );
};

export default OrderPlanView;
